import { useMemo } from 'react';
import { addDays } from 'date-fns';

import { getToday } from '../../shared/utilities/date';
import { useAppSelector } from '../../shared/redux';
import { useGetDossierData } from '../dossier/queries';

import { NB_DAYS_PAYMENT_BALANCE_DATES_SHIFT } from './constants';
import { ContractId, InstalmentViewModel } from './types';
import { calculateDepositDefaultDueDate, getContract } from './selectors';

export const useGetContractTravelInformation = (contractId: ContractId) => {
    const { arrivalDate, departureDate } = useGetDossierData(contractId)?.dossier?.project || {};
    const contract = useAppSelector(getContract);

    return useMemo(() => {
        if (contract === null && (!arrivalDate || !departureDate)) return;

        if (contract === null) {
            return {
                arrivalDate: arrivalDate ?? undefined,
                departureDate: departureDate ?? undefined,
            };
        }

        return {
            legalInformation: contract.legalInformation,
            arrivalDate: contract.travelDates.arrivalDate,
            departureDate: contract.travelDates.departureDate,
            proposal: contract.proposal ? contract.proposal.url : '',
            legalInformationReference: contract.legalInformation?.reference,
        };
    }, [arrivalDate, departureDate, contract]);
};

export const useCalculateBalanceDefaultDueDate = (contractId: ContractId): Date => {
    const arrivalDate = useGetDossierData(contractId)?.dossier?.project?.arrivalDate;

    return useMemo(() => {
        const today = getToday();
        if (!arrivalDate) return today;

        const defaultBalanceDate = addDays(arrivalDate, NB_DAYS_PAYMENT_BALANCE_DATES_SHIFT);
        return defaultBalanceDate < today ? today : defaultBalanceDate;
    }, [arrivalDate]);
};

export const useGetEmptyInstalment = (
    type: string,
    contractId: ContractId,
    currency: string,
    instalment?: InstalmentViewModel,
): InstalmentViewModel => {
    const balanceDefaultDueDate = useCalculateBalanceDefaultDueDate(contractId);

    return useMemo(
        () => ({
            payerName: '',
            label: '',
            dueDate: type === 'deposit' ? calculateDepositDefaultDueDate() : balanceDefaultDueDate,
            price: {
                currency: currency,
                amount: instalment && instalment.price.amount ? instalment.price.amount : '',
            },
            type: type,
            id: '',
            status: 'created',
            paidAt: null,
        }),
        [type, currency, instalment, balanceDefaultDueDate],
    );
};
